import { useTranslation } from "react-i18next"

import { PlusCircle, TrashIcon } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { buttonVariants } from "@/components/ui/button-variants"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { uiOption } from "@/lib/ui-options"
import { cn } from "@/lib/utils"

import { useTranslationDictionary } from "../hooks/use-translation-dictionary"

export function TranslationDictionary() {
  const isDev = uiOption("show_dev_options")

  const { t } = useTranslation()

  const {
    customTranslations,
    addCustomTranslation,
    handleChangeCustomTranslation,
    handleDeleteCustomTranslation,
    acceptedFiles,
    getInputProps,
    openFileSelector,
  } = useTranslationDictionary()

  const jsonStructure = `{
        "${t("translationdictionary.original")}1": "${t("translationdictionary.translation")}1",
        "${t("translationdictionary.original")}2": "${t("translationdictionary.translation")}2",
        ...
}`

  return (
    <section className="flex w-full flex-col gap-1">
      <Label>{t("pages.translate.custom_translations")}</Label>
      {isDev && (
        <>
          <Tooltip>
            <TooltipTrigger
              className={cn(
                buttonVariants({
                  variant: "secondary",
                  className: "w-full max-w-full",
                })
              )}
              onClick={openFileSelector}
            >
              <p className="truncate">
                {acceptedFiles[0]?.name ?? t("translationdictionary.upload") + " JSON"}
              </p>
            </TooltipTrigger>
            <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
              <p className="whitespace-pre-wrap">{jsonStructure}</p>
            </TooltipContent>
          </Tooltip>
          <input {...getInputProps()} />
        </>
      )}
      <Table parentClassName="max-h-[400px]">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[100px]">{t("translationdictionary.original")}</TableHead>
            <TableHead>{t("translationdictionary.translation")}</TableHead>
            <TableHead>
              <Button size="icon" variant="icon" onClick={addCustomTranslation}>
                <PlusCircle className="text-success size-5 shrink-0" />
              </Button>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {customTranslations.map((customTranslation, index) => (
            <TableRow key={index}>
              <TableCell className="font-medium">
                <Input
                  className={cn("border-[1px]")}
                  name="original"
                  value={customTranslation.original}
                  onChange={(e) => handleChangeCustomTranslation(e, index)}
                />
              </TableCell>
              <TableCell>
                <Input
                  name="translation"
                  value={customTranslation.translation}
                  onChange={(e) => handleChangeCustomTranslation(e, index)}
                />
              </TableCell>
              <TableCell>
                <Button
                  size="icon"
                  variant="icon"
                  onClick={() => handleDeleteCustomTranslation(index)}
                >
                  <TrashIcon className="text-destructive size-4 shrink-0" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  )
}
